import React, { createContext, useState, useCallback, useMemo, useEffect } from 'react';
import useLocalStorage from '@hooks/useLocalStorage';
import { appVariables } from '@/apollo/appVariables';

type TimezoneProviderProps = {
  children: JSX.Element;
};
type TimezoneContext = {
  localTimezone: string;
  appliedTimezone?: string;
  preferredTimezone: string;
  preferredTimeSetting?: number;
  eventTimezone: string;
  eventTimezoneLabels: {
    short: string;
    long: string;
  };
  togglePrefTimezone?: (selectedButton: number) => void;
  setEventData?: (timezone: string, timezoneId: string, defaultTimeButton: TimezoneButton) => void;
  setTimezoneFromQuery?: React.Dispatch<React.SetStateAction<string>>;
};
type TimezoneButton = typeof EVENT_TIME_BUTTON | typeof LOCAL_TIME_BUTTON;
type TimezoneSettings = {
  preferredTimezone: string;
  timezoneButton: TimezoneButton;
};
const browserTimezone =
  new Intl.DateTimeFormat().resolvedOptions().timeZone !== 'Etc/Unknown'
    ? new Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'Etc/UTC';

export const EVENT_TIME_BUTTON = 0;
export const LOCAL_TIME_BUTTON = 1;

const defaultPrefTimezone = {
  localTimezone: browserTimezone,
  appliedTimezone: null,
  preferredTimezone: browserTimezone,
  preferredTimeSetting: null,
  eventTimezone: '',
  eventTimezoneLabels: { short: 'Event_Timezone_Abbr_360__resx', long: 'Event_Timezone_Name_360__resx' }
};
export const TimezoneContext = createContext<TimezoneContext>(defaultPrefTimezone);

/**
 * Provides a context for the user's preferred timezone view.
 */
export function TimezoneProvider({ children }: TimezoneProviderProps): JSX.Element {
  const [localTimezone] = useState(defaultPrefTimezone.localTimezone);

  // State for overriding with any time zone
  const [appliedTimezone, setAppliedTimezone] = useState<string>(null);

  // Default based on event and attendee
  //   - In person or hybrid + in person attendee = event time zone
  //   - Virtual or hybrid + virtual attendee = device time zone
  const [eventTimezoneItems, setEventTimezoneItems] = useState<
    TimezoneContext['eventTimezoneLabels'] & {
      eventTimezone: string;
      defaultTimeButton: TimezoneButton;
      defaultTimezone: string;
    }
  >({
    eventTimezone: defaultPrefTimezone.eventTimezone,
    defaultTimeButton: null,
    defaultTimezone: '',
    ...defaultPrefTimezone.eventTimezoneLabels
  });

  // These states are needed to trigger re-renders/effects in all cases when
  // the selected button is toggled.
  // This should handle the case when the event time zone and local time zone
  // are the same.
  const { eventId, attendeeId } = appVariables();
  const [timezoneSettings, setTimezoneSettings] = useLocalStorage<TimezoneSettings>(
    `timezone-${eventId}-${attendeeId}`,
    {
      preferredTimezone: '',
      timezoneButton: null
    }
  );

  const { preferredTimezone = '', timezoneButton = null } = timezoneSettings || {};

  /*
    Resolve timezone preference in this order:
     - appliedTimezone
     - preferredTimezone
     - timezoneButton selection
     - event timezone (depending on event format)
     - browser timezone
  */
  let resolvedTimezoneButton = timezoneButton;
  const resolvedTimezone =
    appliedTimezone ||
    preferredTimezone ||
    (resolvedTimezoneButton === EVENT_TIME_BUTTON && eventTimezoneItems.eventTimezone) ||
    (resolvedTimezoneButton === LOCAL_TIME_BUTTON && localTimezone) ||
    eventTimezoneItems.defaultTimezone ||
    localTimezone;

  if (resolvedTimezoneButton == null) {
    resolvedTimezoneButton = eventTimezoneItems.defaultTimeButton;
  }

  // Set timezone and timezoneButton the first time if no localStorage values are present
  useEffect(() => {
    if (
      timezoneSettings.preferredTimezone === '' &&
      timezoneSettings.timezoneButton === null &&
      resolvedTimezone !== '' &&
      resolvedTimezoneButton !== null
    ) {
      setTimezoneSettings({
        preferredTimezone: resolvedTimezone,
        timezoneButton: resolvedTimezoneButton
      });
    }
  }, [setTimezoneSettings, resolvedTimezone, resolvedTimezoneButton, timezoneSettings]);

  const selectTimezone = useCallback(
    (selectedButton: typeof EVENT_TIME_BUTTON | typeof LOCAL_TIME_BUTTON): void => {
      const newTimezone = selectedButton === EVENT_TIME_BUTTON ? eventTimezoneItems.eventTimezone : localTimezone;

      setAppliedTimezone(newTimezone);
      setTimezoneSettings({
        preferredTimezone: newTimezone,
        timezoneButton: selectedButton
      });
    },
    [eventTimezoneItems.eventTimezone, localTimezone, setTimezoneSettings]
  );

  // This is used to set the event timezone/name when the data comes in
  const updateEventData = useCallback(
    (
      newTimezone: string,
      timezoneId: string,
      defaultTimeButton: typeof EVENT_TIME_BUTTON | typeof LOCAL_TIME_BUTTON
    ): void => {
      if (eventTimezoneItems.eventTimezone !== newTimezone) {
        setEventTimezoneItems({
          eventTimezone: newTimezone,
          short: `Event_Timezone_Abbr_${timezoneId}__resx`,
          long: `Event_Timezone_Name_${timezoneId}__resx`,
          defaultTimeButton,
          defaultTimezone: defaultTimeButton === EVENT_TIME_BUTTON ? newTimezone : localTimezone
        });
      }
    },
    [eventTimezoneItems.eventTimezone, localTimezone]
  );

  const value = useMemo(
    () => ({
      localTimezone,
      appliedTimezone,
      preferredTimezone: resolvedTimezone,
      preferredTimeSetting: resolvedTimezoneButton,
      eventTimezone: eventTimezoneItems.eventTimezone,
      eventTimezoneLabels: {
        short: eventTimezoneItems.short,
        long: eventTimezoneItems.long
      },
      togglePrefTimezone: selectTimezone,
      setEventData: updateEventData,
      setTimezoneFromQuery: setAppliedTimezone
    }),
    [
      appliedTimezone,
      eventTimezoneItems.eventTimezone,
      eventTimezoneItems.long,
      eventTimezoneItems.short,
      localTimezone,
      resolvedTimezoneButton,
      resolvedTimezone,
      selectTimezone,
      updateEventData
    ]
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}
